import React, { useCallback, useContext, useEffect, useState } from "react";
import { PatientGoal } from "../../goal_templates/classes/patient_goal_class";
import { PatientGoalObservation } from "../../goal_templates/classes/patient_goal_observation_class";
import {
  ErrorMessage,
  GoalFormNavigationButton,
  NoSelection,
  AlertBlock,
} from "../../goal_templates/common/components/generalUtilities";
import { TMDataContext } from "../../utils/custom_hooks/TMDataContext";
import { PatientGoalPreviewHandler } from "../../utils/state_handlers";
import { ActivationModalControls } from "../common/components/activationModalControls";

import { PatientGoalEducationPreview } from "./patientGoalEducationPreview";
import { PatientGoalInterviewPreview } from "./patientGoalInterviewPreview";
import { PatientGoalObservationPreview } from "./patientGoalObservationPreview";

export const GoalActivationContainer = ({ selection, closeModal }) => {
  const {
    api,
    t,
    DBObject,
    setSelectedPatientGoals,
    selectedPatientGoals,
  } = useContext(TMDataContext);
  const [goalPreview, setGoalPreview] = useState<any>();
  const [initialReading, setInitialReading] = useState<number | null>(null);

  const [goalName, setGoalName] = useState(
    `${goalPreview ? goalPreview.name : selection.name}`
  );
  const [goalTarget, setGoalTarget] = useState(
    `${
      goalPreview ? goalPreview.achievementTarget : selection.achievementTarget
    }`
  );
  const [hasErrors, setHasErrors] = useState<boolean>(false);
  const [loadFail, setLoadFail] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [hasObservationErrors, setHasObservationErrors] = useState<boolean>(
    false
  );

  useEffect(() => {
    if (selection.type.name.includes("PatientGoal")) {
      setIsLoaded(true);
      setLoadFail(false);
      setGoalPreview(selection);
    } else if (PatientGoal.findByTemplateId(selection.id)) {
      setIsLoaded(true);
      setLoadFail(false);
      let newGoal = PatientGoal.findByTemplateId(selection.id);
      setGoalPreview(newGoal);
    } else {
      setIsLoaded(false);
      api
        .buildGoalPreview(DBObject.PatientId, selection.id)
        .then((payload) => {
          return new PatientGoalPreviewHandler(payload);
        })
        .then((goal) => {
          setIsLoaded(true);
          setLoadFail(false);
          setGoalPreview(goal);
        })
        .catch(() => {
          setIsLoaded(true);
          setLoadFail(true);
        });
    }
  }, []);

  useEffect(() => {
    if (goalTarget == "" || goalName == "" || hasObservationErrors) {
      setHasErrors(true);
    } else {
      setHasErrors(false);
    }
  }, [goalName, goalTarget, hasObservationErrors]);

  const handleGoalName = useCallback((e) => {
    setGoalName(e.target.value);
  }, []);

  const handleInitialReading = (input) => {
    input == "" ? setInitialReading(null) : setInitialReading(input);
  };

  const selectTemplateForSaving = () => {
    goalPreview.name = goalName;
    goalPreview.achievementTarget = goalTarget;
    goalPreview.initialReading = initialReading;
    goalPreview.selectForSaving();
    setSelectedPatientGoals((currentSelection) =>
      addGoalSelection(currentSelection)
    );
    closeModal();
  };

  const addGoalSelection = (currentSelection) => {
    var newSelection = [...currentSelection, goalPreview];
    return newSelection;
  };

  const displayGoal = () => {
    if (!goalPreview)
      return <ErrorMessage message={t("views.goals.errors.no_goal_loaded")} />;
    switch (goalPreview.type.name) {
      case "PatientGoalObservation":
        return (
          <PatientGoalObservationPreview
            goal={goalPreview}
            goalName={goalName}
            handleGoalName={handleGoalName}
            setHasObservationErrors={setHasObservationErrors}
            initialReading={initialReading}
            handleInitialReading={handleInitialReading}
            goalTarget={goalTarget}
            handleGoalTarget={setGoalTarget}
          />
        );
        break;
      case "PatientGoalInterview":
        return (
          <PatientGoalInterviewPreview
            goal={goalPreview}
            goalName={goalName}
            handleGoalName={handleGoalName}
          />
        );
        break;
      case "PatientGoalEducation":
        return (
          <PatientGoalEducationPreview
            goal={goalPreview}
            goalName={goalName}
            handleGoalName={handleGoalName}
          />
        );
        break;
      default:
        return (
          <NoSelection
            message={t("views.goals.errors.new_goal_load_failure")}
          />
        );
    }
  };

  const renderModalBanner = () => {
    return isLoaded
      ? t("views.referral.dialog.goal_preview")
      : t("views.referral.dialog.goal_alert");
  };

  const renderModalContents = () => {
    if (!isLoaded)
      return (
        <div style={{ padding: "10px" }}>{t("progress_messages.loading")}</div>
      );
    if (loadFail)
      return (
        <>
          <AlertBlock
            block={t("views.goals.errors.preview_patient_goal_load_failure")}
          />
          <div className="patient-goal-footer-block">
            <GoalFormNavigationButton
              allow={true}
              handleClick={() => closeModal()}
              title="ok"
            />
          </div>
        </>
      );
    if (isLoaded && !loadFail)
      return (
        <>
          <div className="patient-goal-activation-contents">
            {displayGoal()}
          </div>
          <div className="patient-goal-footer-block">
            <ActivationModalControls
              hasErrors={hasErrors}
              actionLabel="assign"
              action={selectTemplateForSaving}
              closeModal={closeModal}
            />
          </div>
        </>
      );
  };

  return (
    <div className="patient-goal-activation-modal">
      <div className="patient-goal-modal-container ">
        <div className="patient-goal-preview-title">
          <span>{renderModalBanner()}</span>
        </div>
        {renderModalContents()}
      </div>
    </div>
  );
};
